import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Activity, AlertTriangle, TrendingUp, CheckCircle, RefreshCw,
  Filter, Search, ChevronUp, ChevronDown, Target, Brain,
  Zap, Award, BarChart2, Settings, Plus, Trash2, Edit3,
  Shield, Clock, DollarSign, Star, ArrowRight, Save
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell,
  ReferenceLine, ComposedChart, Area, ScatterChart, Scatter,
  ZAxis, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from 'recharts';
import CountUp from 'react-countup';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

// ── Types ─────────────────────────────────────────────────────────────────────
interface Improvement {
  improvement_id: string; region: string; process_name: string;
  baseline_time_hours: number; improved_time_hours: number;
  baseline_cost_fcfa: number; improved_cost_fcfa: number;
  implementation_date: string; savings_realized_fcfa: number;
  quality_impact_score: number; adopted_by_other_units: number;
}
interface DMAICProject {
  id: string; title: string; region: string; process: string;
  phase: 'Define'|'Measure'|'Analyze'|'Improve'|'Control';
  owner: string; startDate: string; targetDate: string;
  gainsFCFA: number; status: 'Active'|'On Hold'|'Completed';
  description: string; sigma_before: number; sigma_after: number;
}
interface KaizenCard {
  id: string; problem: string; impact: string; suggestion: string;
  region: string; submittedBy: string; date: string;
  priority: 'Haute'|'Moyenne'|'Faible'; status: 'Soumis'|'En cours'|'Approuvé'|'Clôturé';
}

const API_URL=(import.meta as any).env?.VITE_API_URL||'https://nexus360-platform-backend-api-production.up.railway.app';
const PAL=['#3b82f6','#10b981','#f97316','#a855f7','#eab308','#ef4444','#06b6d4','#ec4899','#84cc16'];
const REGIONS=['Adamaoua','Centre','Est','Extrême-Nord','Littoral','Nord','Nord-Ouest','Ouest','Sud','Sud-Ouest'];
const DMAIC_COLORS:Record<string,string>={Define:'#3b82f6',Measure:'#a855f7',Analyze:'#f97316',Improve:'#10b981',Control:'#06b6d4'};
const DMAIC_PHASES=['Define','Measure','Analyze','Improve','Control'];

const n=(v:any,fb=0)=>{const x=Number(v);return isNaN(x)?fb:x;};
const fm=(v:number)=>v>=1e9?(v/1e9).toFixed(1)+'B':v>=1e6?(v/1e6).toFixed(1)+'M':v>=1e3?(v/1e3).toFixed(0)+'K':String(Math.round(v));
const ff=(v:number)=>fm(v)+' F';
const timeReduction=(i:Improvement)=>n(i.baseline_time_hours)>0?Math.round((1-n(i.improved_time_hours)/n(i.baseline_time_hours))*1000)/10:0;
const costReduction=(i:Improvement)=>n(i.baseline_cost_fcfa)>0?Math.round((1-n(i.improved_cost_fcfa)/n(i.baseline_cost_fcfa))*1000)/10:0;
const qualityScore=(i:Improvement)=>Math.min(100,Math.round(n(i.quality_impact_score)));
const complianceRate=(i:Improvement)=>Math.min(100,Math.round(60+n(i.quality_impact_score)*0.4));
const reworkRate=(i:Improvement)=>Math.max(0,Math.round(40-n(i.quality_impact_score)*0.4));
const globalQScore=(items:Improvement[])=>items.length?Math.round(items.reduce((s,i)=>s+qualityScore(i),0)/items.length):0;
const sigmaLevel=(score:number)=>score>=95?5:score>=90?4:score>=85?3:score>=75?2:1;
const westernElectric=(values:number[])=>{if(values.length<3)return[];const mean=values.reduce((s,v)=>s+v,0)/values.length;const std=Math.sqrt(values.reduce((s,v)=>s+Math.pow(v-mean,2),0)/values.length);const alerts:number[]=[];for(let i=2;i<values.length;i++){if(Math.abs(values[i-2]-mean)>2*std&&Math.abs(values[i-1]-mean)>2*std&&Math.abs(values[i]-mean)>2*std)alerts.push(i);}return alerts;};

// ── WOW: KPI Card ─────────────────────────────────────────────────────────────
const KPICard=({icon:Icon,label,numValue,suffix='',color,bg,border,decimals=0,alert=false,prefix=''}:
  {icon:any;label:string;numValue:number;suffix?:string;prefix?:string;color:string;bg:string;border:string;decimals?:number;alert?:boolean})=>{
  const glowColor=color.includes('green')?'rgba(16,185,129,0.2)':color.includes('yellow')?'rgba(234,179,8,0.2)':color.includes('orange')?'rgba(249,115,22,0.2)':color.includes('purple')?'rgba(168,85,247,0.2)':color.includes('cyan')?'rgba(6,182,212,0.2)':'rgba(59,130,246,0.2)';
  return(
    <motion.div whileHover={{scale:1.04,boxShadow:`0 0 18px ${glowColor}`}} transition={{duration:.15}}
      className={`rounded-xl p-3 border ${border} ${bg} flex items-center gap-2 cursor-default relative overflow-hidden`}
      style={{backdropFilter:'blur(8px)',background:'linear-gradient(135deg,rgba(15,23,42,0.85) 0%,rgba(30,41,59,0.6) 100%)'}}>
      <motion.div className="absolute top-0 left-0 right-0 h-px opacity-50"
        style={{background:`linear-gradient(90deg,transparent,${color.includes('green')?'#10b981':color.includes('yellow')?'#eab308':color.includes('orange')?'#f97316':color.includes('purple')?'#a855f7':color.includes('cyan')?'#06b6d4':'#3b82f6'},transparent)`}}
        animate={{x:['-100%','100%']}} transition={{duration:2.5,repeat:Infinity,ease:'linear',repeatDelay:1}}/>
      <Icon className={`w-4 h-4 ${color} flex-shrink-0 ${alert?'animate-pulse':''}`}/>
      <div>
        <p className={`font-bold text-base leading-none ${color}`}>
          <CountUp end={numValue} duration={1.5} decimals={decimals} prefix={prefix} suffix={suffix} preserveValue separator=" "/>
        </p>
        <p className="text-slate-400 text-xs mt-0.5">{label}</p>
      </div>
      {alert&&<motion.div className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-orange-400"
        animate={{opacity:[1,.2,1],scale:[1,1.5,1]}} transition={{duration:1,repeat:Infinity}}/>}
    </motion.div>
  );
};

// ── WOW: Skeleton ─────────────────────────────────────────────────────────────
const SkeletonCard=()=>(
  <div className="rounded-xl border border-blue-900/40 bg-slate-800/80 p-4">
    <Skeleton baseColor="#1e293b" highlightColor="#334155" height={14} width="55%" className="mb-3"/>
    <Skeleton baseColor="#1e293b" highlightColor="#334155" height={28} width="35%" className="mb-2"/>
    <Skeleton baseColor="#1e293b" highlightColor="#334155" height={10} count={2}/>
  </div>
);

// ── WOW: LIVE Badge ───────────────────────────────────────────────────────────
const LiveBadge=({lastUpd,refreshing}:{lastUpd:Date;refreshing:boolean})=>(
  <div className="flex items-center gap-2">
    <motion.span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-green-500/20 text-green-300 border border-green-500/30"
      animate={{opacity:refreshing?[1,.5,1]:1}} transition={{duration:.6,repeat:refreshing?Infinity:0}}>
      <motion.span className="w-1.5 h-1.5 rounded-full bg-green-400"
        animate={{opacity:[1,.3,1],scale:[1,1.4,1]}} transition={{duration:1.5,repeat:Infinity}}/>
      LIVE
    </motion.span>
    <span className="text-slate-500 text-xs">{refreshing?'Actualisation…':lastUpd.toLocaleTimeString('fr-FR',{hour:'2-digit',minute:'2-digit'})}</span>
  </div>
);

// ── Card with glassmorphism ───────────────────────────────────────────────────
const Card=({icon:Icon,title,children,accent='text-blue-400',className='',action}:
  {icon:any;title:string;children:React.ReactNode;accent?:string;className?:string;action?:React.ReactNode})=>(
  <motion.div whileHover={{boxShadow:'0 0 24px rgba(59,130,246,0.07)'}}
    className={`rounded-xl border border-blue-900/40 overflow-hidden shadow-lg ${className}`}
    style={{background:'linear-gradient(135deg,rgba(15,23,42,0.9) 0%,rgba(30,41,59,0.65) 100%)',backdropFilter:'blur(8px)'}}>
    <div className="flex items-center gap-2 px-4 py-3 border-b border-blue-900/40"
      style={{background:'linear-gradient(90deg,rgba(30,41,59,0.85) 0%,rgba(15,23,42,0.5) 100%)'}}>
      <Icon className={`w-4 h-4 ${accent}`}/><span className="text-sm font-semibold text-white flex-1">{title}</span>{action}
    </div>
    <div className="p-4">{children}</div>
  </motion.div>
);

const PBar=({value,color='bg-blue-500'}:{value:number;color?:string})=>(
  <div className="w-full bg-slate-700 rounded-full h-1.5 overflow-hidden">
    <motion.div className={`h-1.5 rounded-full ${color}`}
      initial={{width:0}} animate={{width:`${Math.min(100,value)}%`}} transition={{duration:1,ease:'easeOut'}}/>
  </div>
);

// ── Tooltip ───────────────────────────────────────────────────────────────────
const NexTip=({active,payload,label}:any)=>{
  if(!active||!payload?.length)return null;
  return(<motion.div initial={{opacity:0,scale:.95}} animate={{opacity:1,scale:1}} transition={{duration:.12}}
    className="bg-slate-900/95 border border-slate-600 rounded-xl p-3 shadow-2xl text-xs backdrop-blur min-w-36">
    <p className="text-white font-semibold mb-1.5 pb-1 border-b border-slate-700">{label}</p>
    {payload.map((p:any,i:number)=>(<div key={i} className="flex justify-between gap-3 py-0.5"><div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm" style={{background:p.fill||p.stroke||p.color}}/><span className="text-slate-300">{p.name}</span></div><span className="font-bold" style={{color:p.fill||p.stroke||p.color}}>{p.value}</span></div>))}
  </motion.div>);
};

// ── Animated Sigma Gauge ──────────────────────────────────────────────────────
const SigmaGauge=({score}:{score:number})=>{
  const sigma=sigmaLevel(score);
  const color=score>=90?'#10b981':score>=75?'#3b82f6':score>=60?'#eab308':'#ef4444';
  const label=score>=90?'EXCELLENT':score>=75?'BON':score>=60?'MODÉRÉ':'CRITIQUE';
  const r=80,cx=100,cy=105;
  const toR=(d:number)=>d*Math.PI/180;
  const px=(d:number)=>cx+r*Math.cos(toR(d));
  const py=(d:number)=>cy-r*Math.sin(toR(d));
  const needleDeg=180-(score/100)*180;
  const nx=cx+(r-18)*Math.cos(toR(needleDeg));
  const ny=cy-(r-18)*Math.sin(toR(needleDeg));
  return(
    <div className="flex flex-col items-center">
      <svg width="200" height="210" viewBox="0 0 200 210">
        <defs><linearGradient id="sgGrad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#ef4444"/><stop offset="40%" stopColor="#eab308"/>
          <stop offset="70%" stopColor="#3b82f6"/><stop offset="100%" stopColor="#10b981"/>
        </linearGradient></defs>
        <path d={`M${px(180)} ${py(180)} A${r} ${r} 0 0 1 ${px(0)} ${py(0)}`} fill="none" stroke="#1e293b" strokeWidth="14" strokeLinecap="round"/>
        {score>0&&<motion.path d={`M${px(180)} ${py(180)} A${r} ${r} 0 0 1 ${px(needleDeg)} ${py(needleDeg)}`}
          fill="none" stroke="url(#sgGrad)" strokeWidth="14" strokeLinecap="round"
          initial={{pathLength:0,opacity:0}} animate={{pathLength:1,opacity:1}} transition={{duration:2,ease:'easeOut'}}/>}
        {[0,25,50,75,100].map(v=>{
          const deg=180-(v/100)*180;
          const x1=cx+(r-20)*Math.cos(toR(deg)),y1=cy-(r-20)*Math.sin(toR(deg));
          const x2=cx+(r-7)*Math.cos(toR(deg)),y2=cy-(r-7)*Math.sin(toR(deg));
          return <line key={v} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#334155" strokeWidth="1.5"/>;
        })}
        <motion.line x1={cx} y1={cy} x2={cx} y2={cy-(r-18)}
          animate={{x2:nx,y2:ny}} transition={{type:'spring',stiffness:50,damping:15,delay:.6}}
          stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
        <circle cx={cx} cy={cy} r="6" fill="white"/>
        <text x={cx} y={cy+58} textAnchor="middle" fill="white" fontSize="28" fontWeight="bold">{score}</text>
        <text x={cx} y={cy+86} textAnchor="middle" fill={color} fontSize="20" fontWeight="bold">{sigma}σ — {label}</text>
        <text x="14" y="118" fill="#ef4444" fontSize="8">0</text>
        <text x="178" y="118" fill="#10b981" fontSize="8">100</text>
      </svg>
    </div>
  );
};

// ── SPC Chart ─────────────────────────────────────────────────────────────────
const SPCChart=({data,processName}:{data:number[];processName:string})=>{
  const mean=data.length?data.reduce((s,v)=>s+v,0)/data.length:0;
  const std=Math.sqrt(data.reduce((s,v)=>s+Math.pow(v-mean,2),0)/Math.max(data.length,1));
  const ucl=mean+3*std,lcl=Math.max(0,mean-3*std),ucl2=mean+2*std,lcl2=Math.max(0,mean-2*std);
  const alerts=westernElectric(data);
  const chartData=data.map((v,i)=>({idx:`P${i+1}`,value:Math.round(v*10)/10,mean:Math.round(mean*10)/10,ucl:Math.round(ucl*10)/10,lcl:Math.round(lcl*10)/10,ucl2:Math.round(ucl2*10)/10,lcl2:Math.round(lcl2*10)/10,alert:alerts.includes(i)}));
  return(
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-slate-300 text-xs font-medium truncate">{processName}</span>
        {alerts.length>0&&(
          <motion.span className="flex items-center gap-1 text-xs font-bold text-red-400 bg-red-500/10 border border-red-500/30 px-2 py-0.5 rounded-full"
            animate={{opacity:[1,.6,1]}} transition={{duration:1.2,repeat:Infinity}}>
            <AlertTriangle className="w-3 h-3"/> Signal détecté
          </motion.span>
        )}
      </div>
      <ResponsiveContainer width="100%" height={140}>
        <ComposedChart data={chartData} margin={{top:4,right:4,left:0,bottom:4}}>
          <CartesianGrid strokeDasharray="2 2" stroke="#1e3a5f"/>
          <XAxis dataKey="idx" tick={{fill:'#64748b',fontSize:8}}/>
          <YAxis tick={{fill:'#64748b',fontSize:8}} width={28}/>
          <Tooltip content={<NexTip/>}/>
          <ReferenceLine y={ucl} stroke="#ef4444" strokeDasharray="3 2" label={{value:'UCL',fill:'#ef4444',fontSize:7,position:'right'}}/>
          <ReferenceLine y={ucl2} stroke="#f97316" strokeDasharray="2 2" label={{value:'2σ',fill:'#f97316',fontSize:7,position:'right'}}/>
          <ReferenceLine y={mean} stroke="#10b981" strokeDasharray="3 2" label={{value:'X̄',fill:'#10b981',fontSize:7,position:'right'}}/>
          <ReferenceLine y={lcl2} stroke="#f97316" strokeDasharray="2 2" label={{value:'-2σ',fill:'#f97316',fontSize:7,position:'right'}}/>
          <ReferenceLine y={lcl} stroke="#ef4444" strokeDasharray="3 2" label={{value:'LCL',fill:'#ef4444',fontSize:7,position:'right'}}/>
          <Line type="monotone" dataKey="value" name="Valeur" stroke="#3b82f6" strokeWidth={2}
            dot={(p:any)=>(<circle key={p.key} cx={p.cx} cy={p.cy} r={p.payload.alert?6:3} fill={p.payload.alert?'#ef4444':'#3b82f6'} stroke={p.payload.alert?'#fff':'none'} strokeWidth={1}/>)}
            isAnimationActive animationDuration={1200}/>
        </ComposedChart>
      </ResponsiveContainer>
      <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
        <span>X̄={mean.toFixed(1)}</span><span>σ={std.toFixed(1)}</span>
        <span>UCL={ucl.toFixed(1)}</span><span>LCL={lcl.toFixed(1)}</span>
        <motion.span className={alerts.length>0?'text-red-400 font-bold':'text-green-400'}
          animate={alerts.length>0?{opacity:[1,.5,1]}:{}} transition={{duration:1.5,repeat:Infinity}}>
          {alerts.length>0?`${alerts.length} alerte(s) WE`:'Processus stable'}
        </motion.span>
      </div>
    </div>
  );
};

// ── DMAIC Progress ────────────────────────────────────────────────────────────
const DMAICProgress=({phase}:{phase:string})=>{
  const idx=DMAIC_PHASES.indexOf(phase);
  return(
    <div className="flex items-center gap-0.5">
      {DMAIC_PHASES.map((p,i)=>(
        <div key={p} className="flex items-center gap-0.5">
          <motion.div className={`px-1.5 py-0.5 rounded text-xs font-bold transition-all ${i<=idx?'text-white':'text-slate-600 bg-slate-700/40'}`}
            style={{background:i<=idx?DMAIC_COLORS[p]:''}}
            whileHover={{scale:1.1}}>
            {p[0]}
          </motion.div>
          {i<4&&<div className={`w-3 h-0.5 ${i<idx?'bg-blue-500':'bg-slate-700'}`}/>}
        </div>
      ))}
    </div>
  );
};

// ── Initial data ──────────────────────────────────────────────────────────────
const INIT_DMAIC:DMAICProject[]=[
  {id:'D1',title:'Réduction délai traitement plaintes',region:'Littoral',process:'Traitement des plaintes',phase:'Control',owner:'Commissaire Bello',startDate:'2024-01-15',targetDate:'2024-06-15',gainsFCFA:4500000,status:'Completed',description:'Réduire le délai moyen de 24h à 10h via digitalisation des formulaires',sigma_before:2,sigma_after:4},
  {id:'D2',title:'Optimisation archivage judiciaire',region:'Centre',process:'Archivage des dossiers judiciaires',phase:'Improve',owner:'Inspecteur Owona',startDate:'2024-03-01',targetDate:'2024-09-01',gainsFCFA:2800000,status:'Active',description:'Numérisation et indexation des dossiers pour réduire temps de recherche',sigma_before:2,sigma_after:3},
  {id:'D3',title:'Contrôle identités - flux rapide',region:'Extrême-Nord',process:'Contrôle des identités',phase:'Analyze',owner:'Lt. Moussa',startDate:'2024-05-10',targetDate:'2024-11-10',gainsFCFA:1200000,status:'Active',description:'Implémenter voie rapide pour contrôle biométrique',sigma_before:1,sigma_after:2},
  {id:'D4',title:'Processus recrutement accéléré',region:'Est',process:'Processus de recrutement',phase:'Measure',owner:'DRH Nkoa',startDate:'2024-07-01',targetDate:'2025-01-01',gainsFCFA:5200000,status:'Active',description:'Digitaliser les étapes de présélection pour réduire le cycle de 3 mois',sigma_before:2,sigma_after:3},
  {id:'D5',title:'Certificats résidence - 24h',region:'Littoral',process:'Délivrance des certificats de résidence',phase:'Define',owner:'Adj. Fomba',startDate:'2024-09-01',targetDate:'2025-03-01',gainsFCFA:900000,status:'On Hold',description:'Objectif: délivrer tout certificat en moins de 24h via guichet unique',sigma_before:1,sigma_after:2},
];
const INIT_KAIZEN:KaizenCard[]=[
  {id:'K1',problem:'Files d\'attente excessives au commissariat de Douala-Akwa',impact:'2h de délai moyen / citoyen',suggestion:'Implémenter système de rendez-vous en ligne',region:'Littoral',submittedBy:'Agent Mbarga',date:'2024-11-01',priority:'Haute',status:'En cours'},
  {id:'K2',problem:'Formulaires de plainte toujours en papier',impact:'Perte de dossiers, délais',suggestion:'Tablette numérique au guichet',region:'Centre',submittedBy:'Sgt. Ateba',date:'2024-11-15',priority:'Haute',status:'Approuvé'},
  {id:'K3',problem:'Rapport d\'enquête non standardisé par région',impact:'Incompatibilité des données',suggestion:'Template Word standardisé + formation',region:'Est',submittedBy:'Insp. Daga',date:'2024-10-20',priority:'Moyenne',status:'Soumis'},
  {id:'K4',problem:'Archives physiques inaccessibles hors horaires bureau',impact:'Ralentissement enquêtes urgentes',suggestion:'Scanner et stocker sur serveur DGSN',region:'Nord',submittedBy:'Lt. Bello',date:'2024-11-05',priority:'Moyenne',status:'Soumis'},
  {id:'K5',problem:'Double saisie des données gardés à vue',impact:'Erreurs et perte de temps',suggestion:'Interface unique connectée au SIGD',region:'Adamaoua',submittedBy:'Adj. Fomba',date:'2024-11-20',priority:'Faible',status:'Soumis'},
];

// ── MAIN PAGE ─────────────────────────────────────────────────────────────────
export const LeanAnalyticsPage=({user}:{user:any})=>{
  const[items,setItems]=useState<Improvement[]>([]);
  const[loading,setLoading]=useState(true);
  const[refreshing,setRefreshing]=useState(false);
  const[lastUpd,setLastUpd]=useState(new Date());
  const[regionF,setRegionF]=useState('');
  const[processF,setProcessF]=useState('');
  const[search,setSearch]=useState('');
  const[activeTab,setActiveTab]=useState<'kpis'|'spc'|'dmaic'|'kaizen'>('kpis');
  const[dmaic,setDmaic]=useState<DMAICProject[]>(INIT_DMAIC);
  const[kaizen,setKaizen]=useState<KaizenCard[]>(INIT_KAIZEN);
  const[showDMAICForm,setShowDMAICForm]=useState(false);
  const[showKaizenForm,setShowKaizenForm]=useState(false);
  const[editDMAIC,setEditDMAIC]=useState<DMAICProject|null>(null);
  const[kaizenForm,setKaizenForm]=useState({problem:'',impact:'',suggestion:'',region:'Littoral',submittedBy:''});
  const[newDMAIC,setNewDMAIC]=useState<Partial<DMAICProject>>({phase:'Define',status:'Active',gainsFCFA:0,sigma_before:1,sigma_after:1});

  const fetchData=useCallback(async()=>{
    setRefreshing(true);
    try{
      const res=await fetch(`${API_URL}/api/lean/improvements`);
      const json=await res.json();
      if(json.success&&Array.isArray(json.data)){
        setItems(json.data.map((i:any)=>({...i,baseline_time_hours:n(i.baseline_time_hours),improved_time_hours:n(i.improved_time_hours),baseline_cost_fcfa:n(i.baseline_cost_fcfa),improved_cost_fcfa:n(i.improved_cost_fcfa),savings_realized_fcfa:n(i.savings_realized_fcfa),quality_impact_score:n(i.quality_impact_score),adopted_by_other_units:n(i.adopted_by_other_units)})));
      }
    }catch(e){console.error(e);}
    finally{setLoading(false);setRefreshing(false);setLastUpd(new Date());}
  },[]);
  useEffect(()=>{fetchData();},[fetchData]);

  const filtered=useMemo(()=>items.filter(i=>{
    if(regionF&&i.region!==regionF)return false;
    if(processF&&i.process_name!==processF)return false;
    if(search&&![i.region,i.process_name,i.improvement_id].join(' ').toLowerCase().includes(search.toLowerCase()))return false;
    return true;
  }),[items,regionF,processF,search]);

  const processes=[...new Set(items.map(i=>i.process_name))].filter(Boolean).sort();
  const total=filtered.length;
  const totalSavings=filtered.reduce((s,i)=>s+n(i.savings_realized_fcfa),0);
  const avgQuality=globalQScore(filtered);
  const avgCompliance=filtered.length?Math.round(filtered.reduce((s,i)=>s+complianceRate(i),0)/filtered.length):0;
  const avgRework=filtered.length?Math.round(filtered.reduce((s,i)=>s+reworkRate(i),0)/filtered.length):0;
  const avgTimeReduction=filtered.length?Math.round(filtered.reduce((s,i)=>s+timeReduction(i),0)/filtered.length*10)/10:0;
  const sigma=sigmaLevel(avgQuality);

  const regionKPIs=useMemo(()=>REGIONS.map(r=>{const ri=filtered.filter(i=>i.region===r);if(!ri.length)return null;return{region:r.replace('Extrême-Nord','Ex-Nord').replace('Nord-Ouest','N-Ouest').replace('Sud-Ouest','S-Ouest'),quality:globalQScore(ri),compliance:Math.round(ri.reduce((s,i)=>s+complianceRate(i),0)/ri.length),rework:Math.round(ri.reduce((s,i)=>s+reworkRate(i),0)/ri.length),savings:ri.reduce((s,i)=>s+n(i.savings_realized_fcfa),0),count:ri.length};}).filter(Boolean).sort((a:any,b:any)=>b.quality-a.quality),[filtered]);
  const processKPIs=useMemo(()=>processes.map((p,i)=>{const pi=filtered.filter(x=>x.process_name===p);if(!pi.length)return null;return{process:p.slice(0,24),fullName:p,quality:globalQScore(pi),timeRed:Math.round(pi.reduce((s,i)=>s+timeReduction(i),0)/pi.length*10)/10,savings:pi.reduce((s,i)=>s+n(i.savings_realized_fcfa),0),adopted:Math.round(pi.reduce((s,i)=>s+n(i.adopted_by_other_units),0)/pi.length*10)/10,color:PAL[i%PAL.length]};}).filter(Boolean),[filtered,processes]);
  const spcByProcess=useMemo(()=>{const m:Record<string,number[]>={};[...items].sort((a,b)=>a.implementation_date.localeCompare(b.implementation_date)).forEach(i=>{if(!m[i.process_name])m[i.process_name]=[];m[i.process_name].push(qualityScore(i));});return m;},[items]);
  const savingsTrend=useMemo(()=>{const m:Record<string,number>={};filtered.forEach(i=>{const ym=String(i.implementation_date).slice(0,7);m[ym]=(m[ym]||0)+n(i.savings_realized_fcfa);});return Object.entries(m).sort().slice(-15).map(([month,savings])=>({month,savings:Math.round(savings/1000000*10)/10}));},[filtered]);
  const fu={initial:{opacity:0,y:20},animate:{opacity:1,y:0},transition:{duration:.5}};
  const TABS=[{k:'kpis',label:'Tableau de bord',icon:BarChart2},{k:'spc',label:'Contrôle SPC',icon:Activity},{k:'dmaic',label:'Espace DMAIC',icon:Target},{k:'kaizen',label:'Application Kaizen',icon:Zap}];

  // ── Skeleton Loader ──
  if(loading) return(
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex items-center gap-3 mb-4">
        <Skeleton baseColor="#1e293b" highlightColor="#334155" width={280} height={28}/>
        <Skeleton baseColor="#1e293b" highlightColor="#334155" width={60} height={22} borderRadius={20}/>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">{Array(6).fill(0).map((_,i)=><SkeletonCard key={i}/>)}</div>
      <div className="text-center text-slate-500 text-sm flex items-center justify-center gap-2 pt-4">
        <RefreshCw className="w-4 h-4 animate-spin text-green-400"/>Chargement LEAN Six Sigma…
      </div>
    </div>
  );

  return(
    <div className="p-4 md:p-6 space-y-4" style={{background:'linear-gradient(180deg,rgba(2,6,23,0.25) 0%,transparent 100%)'}}>

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <motion.div animate={{rotate:[0,360]}} transition={{duration:4,repeat:Infinity,ease:'linear'}}>
              <Settings className="w-6 h-6 text-green-400"/>
            </motion.div>
            <h1 className="text-2xl font-bold text-white">LEAN Six Sigma — Amélioration Continue</h1>
          </div>
          <p className="text-slate-400 text-sm">SPC · DMAIC · Kaizen · {total} améliorations</p>
        </div>
        <div className="flex items-center gap-3">
          <LiveBadge lastUpd={lastUpd} refreshing={refreshing}/>
          <button onClick={fetchData} disabled={refreshing}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 text-blue-300 text-xs transition-colors disabled:opacity-50">
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing?'animate-spin':''}`}/>Actualiser
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 rounded-xl p-1 flex-wrap"
        style={{background:'linear-gradient(135deg,rgba(30,41,59,0.8) 0%,rgba(15,23,42,0.6) 100%)',backdropFilter:'blur(8px)',border:'1px solid rgba(59,130,246,0.15)'}}>
        {TABS.map(t=>(
          <motion.button key={t.k} onClick={()=>setActiveTab(t.k as any)}
            whileHover={{scale:1.03}} whileTap={{scale:.97}}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-all ${activeTab===t.k?'bg-blue-600 text-white shadow-lg':'text-slate-400 hover:text-white'}`}>
            <t.icon className="w-3.5 h-3.5"/>{t.label}
          </motion.button>
        ))}
      </div>

      {/* Filters */}
      <motion.div {...fu} className="rounded-xl border border-blue-900/40 p-3 flex flex-wrap gap-3 items-center"
        style={{background:'linear-gradient(135deg,rgba(15,23,42,0.8) 0%,rgba(30,41,59,0.5) 100%)',backdropFilter:'blur(8px)'}}>
        <Filter className="w-4 h-4 text-blue-400 flex-shrink-0"/>
        <div className="relative"><Search className="w-3 h-3 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2"/>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Région / processus…"
            className="bg-slate-700/60 border border-slate-600/40 rounded-lg text-xs text-white pl-6 pr-3 py-1 w-36 focus:outline-none focus:border-blue-500/60"/>
        </div>
        {[{v:regionF,s:setRegionF,o:REGIONS,p:'Région'},{v:processF,s:setProcessF,o:processes,p:'Processus'}].map((f,i)=>(
          <select key={i} value={f.v} onChange={e=>f.s(e.target.value)}
            className="bg-slate-700/60 border border-slate-600/40 rounded-lg text-xs text-slate-300 px-2 py-1 focus:outline-none focus:border-blue-500/60">
            <option value="">{f.p}</option>{f.o.map(o=><option key={o} value={o}>{o}</option>)}
          </select>
        ))}
        {(search||regionF||processF)&&<button onClick={()=>{setSearch('');setRegionF('');setProcessF('');}} className="text-xs text-slate-400 hover:text-white px-2 py-1 rounded-lg border border-slate-600/40">✕ Effacer</button>}
      </motion.div>

      <AnimatePresence mode="wait">

      {/* ══ TAB: KPIs ══ */}
      {activeTab==='kpis'&&(
        <motion.div key="kpis" initial={{opacity:0,y:16}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-16}} transition={{duration:.35}} className="space-y-4">

          {/* KPI Bar with CountUp */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <KPICard icon={Shield}       label="Score Qualité Global"  numValue={avgQuality}         suffix="/100"  color="text-blue-400"   bg="bg-blue-500/10"   border="border-blue-500/30"/>
            <KPICard icon={CheckCircle}  label="Taux de conformité"    numValue={avgCompliance}       suffix="%"     color="text-green-400"  bg="bg-green-500/10"  border="border-green-500/30"/>
            <KPICard icon={Clock}        label="Réduction délais"      numValue={avgTimeReduction}    suffix="%" prefix="-" decimals={1} color="text-cyan-400"   bg="bg-cyan-500/10"   border="border-cyan-500/30"/>
            <KPICard icon={AlertTriangle}label="Taux de reprises"      numValue={avgRework}           suffix="%"     color="text-orange-400" bg="bg-orange-500/10" border="border-orange-500/30" alert={avgRework>20}/>
            <KPICard icon={DollarSign}   label="Économies (M FCFA)"    numValue={Math.round(totalSavings/1e6*10)/10} suffix="M F" decimals={1} color="text-yellow-400" bg="bg-yellow-500/10" border="border-yellow-500/30"/>
            <KPICard icon={Star}         label="Niveau Sigma"           numValue={sigma}              suffix="σ"     color="text-purple-400" bg="bg-purple-500/10" border="border-purple-500/30"/>
          </div>

          {/* Gauge + Region bar */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card icon={Star} title="Score Qualité Global & Niveau Sigma" accent="text-purple-400">
              <div className="flex flex-col items-center pt-0 pb-2 h-full justify-between">
                <SigmaGauge score={avgQuality}/>
                <div className="grid grid-cols-2 gap-2 mt-auto pt-4 w-full">
                  <motion.div whileHover={{scale:1.05}} className="rounded-lg bg-green-500/10 border border-green-500/30 p-2 text-center">
                    <p className="text-green-400 font-bold text-lg"><CountUp end={avgCompliance} duration={1.2} suffix="%" preserveValue/></p>
                    <p className="text-slate-400 text-xs">Conformité</p>
                  </motion.div>
                  <motion.div whileHover={{scale:1.05}} className="rounded-lg bg-orange-500/10 border border-orange-500/30 p-2 text-center">
                    <p className="text-orange-400 font-bold text-lg"><CountUp end={avgRework} duration={1.2} suffix="%" preserveValue/></p>
                    <p className="text-slate-400 text-xs">Reprises</p>
                  </motion.div>
                </div>
              </div>
            </Card>
            <div className="h-full">
              <Card icon={BarChart2} title="KPIs par Région — Score qualité & Conformité" accent="text-blue-400" className="h-full">
                <ResponsiveContainer width="100%" height={340}>
                  <BarChart data={regionKPIs} layout="vertical" margin={{top:4,right:8,left:0,bottom:4}} barCategoryGap="20%" barGap={2}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" horizontal={false}/>
                    <XAxis type="number" domain={[0,100]} tick={{fill:'#94a3b8',fontSize:9}} tickFormatter={v=>`${v}%`}/>
                    <YAxis type="category" dataKey="region" tick={{fill:'#cbd5e1',fontSize:9}} width={80}/>
                    <Tooltip content={<NexTip/>}/>
                    <Legend wrapperStyle={{fontSize:'9px',paddingTop:'4px'}}/>
                    <ReferenceLine x={80} stroke="#10b981" strokeDasharray="3 2" label={{value:'Cible',fill:'#10b981',fontSize:7}}/>
                    <Bar dataKey="quality" name="Score qualité (%)" fill="#3b82f6" barSize={10} isAnimationActive animationDuration={800}>
                      {regionKPIs.map((_:any,i:number)=><Cell key={i} fill={PAL[i%PAL.length]}/>)}
                    </Bar>
                    <Bar dataKey="compliance" name="Taux conformité (%)" fill="#10b981" barSize={10} isAnimationActive animationDuration={1000}/>
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </div>
            <Card icon={TrendingUp} title="Performance par processus — Réduction délais & Économies" accent="text-yellow-400">
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={processKPIs} layout="vertical" margin={{top:4,right:8,left:0,bottom:4}}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" horizontal={false}/>
                  <XAxis type="number" tick={{fill:'#94a3b8',fontSize:9}}/>
                  <YAxis type="category" dataKey="process" tick={{fill:'#cbd5e1',fontSize:8}} width={130}/>
                  <Tooltip content={<NexTip/>}/>
                  <Legend wrapperStyle={{fontSize:'9px',paddingTop:'4px'}}/>
                  <Bar dataKey="timeRed" name="Réduction délai (%)" fill="#f97316" barSize={10} isAnimationActive animationDuration={900}/>
                  <Line type="monotone" dataKey="adopted" name="Unités adoptantes" stroke="#a855f7" strokeWidth={2} dot={{r:3}} isAnimationActive animationDuration={1500}/>
                </ComposedChart>
              </ResponsiveContainer>
            </Card>
          </div>

          {/* Process + Savings */}
          <div className="">
              <Card icon={DollarSign} title="Tendance des économies réalisées (M FCFA)" accent="text-green-400">
              <ResponsiveContainer width="100%" height={340}>
                <ComposedChart data={savingsTrend} margin={{top:8,right:8,left:0,bottom:8}}>
                  <defs><linearGradient id="savGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/><stop offset="95%" stopColor="#10b981" stopOpacity={0}/></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f"/>
                  <XAxis dataKey="month" tick={{fill:'#94a3b8',fontSize:8}} angle={0} textAnchor="middle" height={24} interval="preserveStartEnd"/>
                  <YAxis tick={{fill:'#94a3b8',fontSize:9}} tickFormatter={v=>`${v}M`}/>
                  <Tooltip content={<NexTip/>} formatter={(v:any)=>`${v}M FCFA`}/>
                  <Area type="monotone" dataKey="savings" name="Économies (M FCFA)" stroke="#10b981" strokeWidth={2.5} fill="url(#savGrad)" dot={{r:3}} isAnimationActive animationDuration={1500}/>
                </ComposedChart>
              </ResponsiveContainer>
            </Card>
          </div>

          {/* Improvements table */}
          <Card icon={Settings} title="Registre des améliorations de processus" accent="text-blue-400">
            <div className="overflow-x-auto">
              <div className="overflow-y-auto" style={{maxHeight:'400px'}}>
                <table className="w-full text-xs min-w-max">
                  <thead className="sticky top-0 z-10">
                    <tr className="border-b border-blue-900/60 bg-slate-700">
                      {['ID','Processus','Région','Réd. temps (%)','Réd. coût (%)','Économies','Score qualité','Conformité (%)','Reprises (%)','Unités adoptantes'].map(h=>(
                        <th key={h} className="px-3 py-2.5 text-left text-blue-300 font-semibold whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((i,idx)=>{
                      const tr=timeReduction(i),cr=costReduction(i),qs=qualityScore(i),comp=complianceRate(i),rw=reworkRate(i);
                      return(
                        <motion.tr key={i.improvement_id} initial={{opacity:0}} animate={{opacity:1}} transition={{delay:idx*.01}}
                          whileHover={{backgroundColor:'rgba(59,130,246,0.05)'}}
                          className={`border-b border-blue-900/30 transition-colors ${idx%2===0?'bg-slate-800/40':'bg-slate-700/30'}`}>
                          <td className="px-3 py-2 text-slate-400 whitespace-nowrap font-mono text-xs">{i.improvement_id.slice(0,12)}</td>
                          <td className="px-3 py-2 text-white max-w-40 truncate" title={i.process_name}>{i.process_name}</td>
                          <td className="px-3 py-2 text-slate-300 whitespace-nowrap">{i.region}</td>
                          <td className="px-3 py-2 text-right"><motion.span className={`font-bold ${tr>=30?'text-green-400':tr>=15?'text-blue-400':'text-yellow-400'}`}
                            animate={tr>=30?{opacity:[1,.7,1]}:{}} transition={{duration:2,repeat:Infinity}}>{tr}%</motion.span></td>
                          <td className="px-3 py-2 text-right"><span className={`font-bold ${cr>=30?'text-green-400':cr>=15?'text-blue-400':'text-yellow-400'}`}>{cr}%</span></td>
                          <td className="px-3 py-2 text-green-400 text-right font-bold">{ff(n(i.savings_realized_fcfa))}</td>
                          <td className="px-3 py-2 whitespace-nowrap min-w-24"><div className="flex items-center gap-1.5"><PBar value={qs} color={qs>=80?'bg-green-500':qs>=60?'bg-blue-500':'bg-yellow-500'}/><span className={qs>=80?'text-green-400':qs>=60?'text-blue-400':'text-yellow-400'}>{qs}</span></div></td>
                          <td className="px-3 py-2 text-right"><span className={comp>=80?'text-green-400':comp>=60?'text-blue-400':'text-red-400'}>{comp}%</span></td>
                          <td className="px-3 py-2 text-right"><span className={rw<=10?'text-green-400':rw<=20?'text-yellow-400':'text-red-400'}>{rw}%</span></td>
                          <td className="px-3 py-2 text-center text-purple-400 font-bold">{n(i.adopted_by_other_units)}</td>
                        </motion.tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </Card>
        </motion.div>
      )}

      {/* ══ TAB: SPC ══ */}
      {activeTab==='spc'&&(
        <motion.div key="spc" initial={{opacity:0,y:16}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-16}} transition={{duration:.35}} className="space-y-4">
          <div className="rounded-xl border border-blue-900/40 p-3"
            style={{background:'linear-gradient(135deg,rgba(15,23,42,0.8) 0%,rgba(30,41,59,0.5) 100%)',backdropFilter:'blur(8px)'}}>
            <div className="flex items-center gap-3 flex-wrap">
              <Activity className="w-4 h-4 text-cyan-400"/>
              <span className="text-sm font-semibold text-white">Cartes de contrôle Shewhart — Règles Western Electric</span>
              <div className="flex items-center gap-2 ml-auto text-xs">
                <span className="w-2 h-2 rounded-full bg-red-500"/><span className="text-slate-400">Signal réel (&gt;2σ / 3 périodes)</span>
                <span className="w-2 h-2 rounded-full bg-blue-500 ml-2"/><span className="text-slate-400">Bruit statistique</span>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {Object.entries(spcByProcess).map(([process,values])=>(
              <Card key={process} icon={Activity} title={`SPC: ${process.slice(0,30)}`} accent="text-cyan-400">
                <SPCChart data={values} processName={process}/>
              </Card>
            ))}
          </div>
        </motion.div>
      )}

      {/* ══ TAB: DMAIC ══ */}
      {activeTab==='dmaic'&&(
        <motion.div key="dmaic" initial={{opacity:0,y:16}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-16}} transition={{duration:.35}} className="space-y-4">
          <div className="grid grid-cols-5 gap-3">
            {DMAIC_PHASES.map((phase,pi)=>{
              const count=dmaic.filter(d=>d.phase===phase).length;
              return(
                <motion.div key={phase} whileHover={{scale:1.05,boxShadow:`0 0 16px ${DMAIC_COLORS[phase]}44`}}
                  className="rounded-xl border p-3 text-center cursor-default"
                  style={{background:`${DMAIC_COLORS[phase]}22`,borderColor:`${DMAIC_COLORS[phase]}55`}}>
                  <motion.p className="text-white font-bold text-xl"
                    animate={count>0?{scale:[1,1.1,1]}:{}} transition={{duration:.5,delay:pi*.1}}>
                    <CountUp end={count} duration={1} preserveValue/>
                  </motion.p>
                  <p className="text-xs font-bold mt-0.5" style={{color:DMAIC_COLORS[phase]}}>{phase}</p>
                </motion.div>
              );
            })}
          </div>
          <div className="flex items-center justify-between">
            <h3 className="text-white font-semibold text-sm">Projets DMAIC actifs</h3>
            <button onClick={()=>{setEditDMAIC(null);setNewDMAIC({phase:'Define',status:'Active',gainsFCFA:0,sigma_before:1,sigma_after:1});setShowDMAICForm(true);}}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-500/20 hover:bg-green-500/30 border border-green-500/30 text-green-300 text-xs transition-colors">
              <Plus className="w-3.5 h-3.5"/>Nouveau projet
            </button>
          </div>
          <div className="space-y-3">
            {dmaic.map((d,i)=>(
              <motion.div key={d.id} initial={{opacity:0,x:-12}} animate={{opacity:1,x:0}} transition={{delay:i*.05}}
                whileHover={{boxShadow:'0 0 16px rgba(59,130,246,0.08)'}}
                className="rounded-xl border border-blue-900/40 p-4"
                style={{background:'linear-gradient(135deg,rgba(15,23,42,0.9) 0%,rgba(30,41,59,0.65) 100%)',backdropFilter:'blur(8px)'}}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-white font-semibold text-sm">{d.title}</span>
                      <motion.span className={`px-2 py-0.5 rounded-full text-xs font-bold border ${d.status==='Completed'?'bg-green-500/15 border-green-500/40 text-green-400':d.status==='Active'?'bg-blue-500/15 border-blue-500/40 text-blue-400':'bg-yellow-500/15 border-yellow-500/40 text-yellow-400'}`}
                        animate={d.status==='Active'?{opacity:[1,.7,1]}:{}} transition={{duration:2,repeat:Infinity}}>
                        {d.status}
                      </motion.span>
                    </div>
                    <p className="text-slate-400 text-xs mb-2">{d.description}</p>
                    <div className="flex items-center gap-4 text-xs text-slate-400 flex-wrap">
                      <span>{d.region}</span><span>·</span><span>{d.process}</span><span>·</span>
                      <span>Owner: {d.owner}</span><span>·</span>
                      <span className="text-green-400 font-bold">{ff(d.gainsFCFA)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="flex items-center gap-1 text-xs">
                      <span className="text-slate-400">{d.sigma_before}σ</span>
                      <ArrowRight className="w-3 h-3 text-green-400"/>
                      <span className="text-green-400 font-bold">{d.sigma_after}σ</span>
                    </div>
                    <button onClick={()=>{setEditDMAIC(d);setNewDMAIC(d);setShowDMAICForm(true);}} className="p-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-400 hover:text-white transition-colors"><Edit3 className="w-3 h-3"/></button>
                    <button onClick={()=>setDmaic(prev=>prev.filter(x=>x.id!==d.id))} className="p-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-colors"><Trash2 className="w-3 h-3"/></button>
                  </div>
                </div>
                <DMAICProgress phase={d.phase}/>
              </motion.div>
            ))}
          </div>
          <AnimatePresence>
            {showDMAICForm&&(
              <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
                className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                onClick={e=>{if(e.target===e.currentTarget)setShowDMAICForm(false);}}>
                <motion.div initial={{scale:.95,opacity:0}} animate={{scale:1,opacity:1}} exit={{scale:.95,opacity:0}}
                  className="border border-blue-900/40 rounded-2xl p-6 w-full max-w-lg shadow-2xl"
                  style={{background:'linear-gradient(135deg,rgba(15,23,42,0.98) 0%,rgba(30,41,59,0.9) 100%)',backdropFilter:'blur(16px)'}}>
                  <h3 className="text-white font-bold text-lg mb-4">{editDMAIC?'Modifier projet DMAIC':'Nouveau projet DMAIC'}</h3>
                  <div className="space-y-3">
                    {[{label:'Titre du projet',key:'title',type:'text'},{label:'Processus',key:'process',type:'text'},{label:'Owner',key:'owner',type:'text'}].map(f=>(
                      <div key={f.key}>
                        <label className="text-slate-400 text-xs mb-1 block">{f.label}</label>
                        <input type={f.type} value={(newDMAIC as any)[f.key]||''} onChange={e=>setNewDMAIC(p=>({...p,[f.key]:e.target.value}))}
                          className="w-full bg-slate-700/60 border border-slate-600/40 rounded-lg text-sm text-white px-3 py-2 focus:outline-none focus:border-blue-500/60"/>
                      </div>
                    ))}
                    <div className="grid grid-cols-2 gap-3">
                      <div><label className="text-slate-400 text-xs mb-1 block">Région</label>
                        <select value={newDMAIC.region||''} onChange={e=>setNewDMAIC(p=>({...p,region:e.target.value}))} className="w-full bg-slate-700/60 border border-slate-600/40 rounded-lg text-sm text-slate-300 px-3 py-2 focus:outline-none">
                          {REGIONS.map(r=><option key={r} value={r}>{r}</option>)}
                        </select>
                      </div>
                      <div><label className="text-slate-400 text-xs mb-1 block">Phase DMAIC</label>
                        <select value={newDMAIC.phase||'Define'} onChange={e=>setNewDMAIC(p=>({...p,phase:e.target.value as any}))} className="w-full bg-slate-700/60 border border-slate-600/40 rounded-lg text-sm text-slate-300 px-3 py-2 focus:outline-none">
                          {DMAIC_PHASES.map(p=><option key={p} value={p}>{p}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><label className="text-slate-400 text-xs mb-1 block">Gains estimés (FCFA)</label>
                        <input type="number" value={newDMAIC.gainsFCFA||0} onChange={e=>setNewDMAIC(p=>({...p,gainsFCFA:Number(e.target.value)}))} className="w-full bg-slate-700/60 border border-slate-600/40 rounded-lg text-sm text-white px-3 py-2 focus:outline-none"/>
                      </div>
                      <div><label className="text-slate-400 text-xs mb-1 block">Statut</label>
                        <select value={newDMAIC.status||'Active'} onChange={e=>setNewDMAIC(p=>({...p,status:e.target.value as any}))} className="w-full bg-slate-700/60 border border-slate-600/40 rounded-lg text-sm text-slate-300 px-3 py-2 focus:outline-none">
                          {['Active','On Hold','Completed'].map(s=><option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                    </div>
                    <div><label className="text-slate-400 text-xs mb-1 block">Description</label>
                      <textarea value={newDMAIC.description||''} onChange={e=>setNewDMAIC(p=>({...p,description:e.target.value}))} rows={3} className="w-full bg-slate-700/60 border border-slate-600/40 rounded-lg text-sm text-white px-3 py-2 focus:outline-none resize-none"/>
                    </div>
                  </div>
                  <div className="flex gap-3 mt-5">
                    <button onClick={()=>setShowDMAICForm(false)} className="flex-1 px-4 py-2 rounded-lg border border-slate-600/40 text-slate-400 hover:text-white text-sm transition-colors">Annuler</button>
                    <button onClick={()=>{
                      if(editDMAIC){setDmaic(prev=>prev.map(x=>x.id===editDMAIC.id?{...x,...newDMAIC}:x));}
                      else{setDmaic(prev=>[...prev,{...newDMAIC,id:`D${Date.now()}`,startDate:new Date().toISOString().slice(0,10),targetDate:'',sigma_before:1,sigma_after:1} as DMAICProject]);}
                      setShowDMAICForm(false);
                    }} className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm transition-colors">
                      <Save className="w-4 h-4"/>{editDMAIC?'Mettre à jour':'Créer projet'}
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}

      {/* ══ TAB: KAIZEN ══ */}
      {activeTab==='kaizen'&&(
        <motion.div key="kaizen" initial={{opacity:0,y:16}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-16}} transition={{duration:.35}} className="space-y-4">
          <Card icon={Zap} title="Formulaire Kaizen — Soumission d'amélioration" accent="text-yellow-400"
            action={<motion.button whileHover={{scale:1.05}} onClick={()=>setShowKaizenForm(s=>!s)}
              className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-yellow-500/20 border border-yellow-500/30 text-yellow-300 text-xs hover:bg-yellow-500/30 transition-colors">
              <Plus className="w-3 h-3"/>Nouvelle idée
            </motion.button>}>
            <AnimatePresence>
              {showKaizenForm&&(
                <motion.div initial={{height:0,opacity:0}} animate={{height:'auto',opacity:1}} exit={{height:0,opacity:0}} className="overflow-hidden">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    {[{label:'Problème identifié',key:'problem',ph:'Décrivez le problème observé...'},{label:'Impact estimé',key:'impact',ph:'Quel est l\'impact sur les opérations?'},{label:'Suggestion d\'amélioration',key:'suggestion',ph:'Votre proposition de solution...'}].map(f=>(
                      <div key={f.key}>
                        <label className="text-slate-400 text-xs mb-1.5 block font-medium">{f.label}</label>
                        <textarea value={(kaizenForm as any)[f.key]} onChange={e=>setKaizenForm(p=>({...p,[f.key]:e.target.value}))} rows={3} placeholder={f.ph}
                          className="w-full bg-slate-700/60 border border-slate-600/40 rounded-lg text-sm text-white px-3 py-2 focus:outline-none focus:border-yellow-500/60 resize-none placeholder:text-slate-600"/>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-3 items-end flex-wrap">
                    <div><label className="text-slate-400 text-xs mb-1 block">Région</label>
                      <select value={kaizenForm.region} onChange={e=>setKaizenForm(p=>({...p,region:e.target.value}))} className="bg-slate-700/60 border border-slate-600/40 rounded-lg text-sm text-slate-300 px-3 py-2 focus:outline-none">
                        {REGIONS.map(r=><option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                    <div><label className="text-slate-400 text-xs mb-1 block">Soumis par</label>
                      <input value={kaizenForm.submittedBy} onChange={e=>setKaizenForm(p=>({...p,submittedBy:e.target.value}))} placeholder="Votre nom"
                        className="bg-slate-700/60 border border-slate-600/40 rounded-lg text-sm text-white px-3 py-2 focus:outline-none w-36"/>
                    </div>
                    <motion.button whileHover={{scale:1.04}} whileTap={{scale:.96}} onClick={()=>{
                      if(!kaizenForm.problem.trim())return;
                      setKaizen(prev=>[{id:`K${Date.now()}`,problem:kaizenForm.problem,impact:kaizenForm.impact,suggestion:kaizenForm.suggestion,region:kaizenForm.region,submittedBy:kaizenForm.submittedBy||'Anonyme',date:new Date().toISOString().slice(0,10),priority:'Moyenne',status:'Soumis'},...prev]);
                      setKaizenForm({problem:'',impact:'',suggestion:'',region:'Littoral',submittedBy:''});
                      setShowKaizenForm(false);
                    }} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-yellow-500/20 border border-yellow-500/30 hover:bg-yellow-500/30 text-yellow-300 text-sm transition-colors font-medium">
                      <Zap className="w-4 h-4"/>Soumettre l'idée
                    </motion.button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </Card>

          {/* Kaizen stats */}
          <div className="grid grid-cols-4 gap-3">
            {(['Soumis','En cours','Approuvé','Clôturé'] as const).map((s,i)=>{
              const count=kaizen.filter(k=>k.status===s).length;
              const colors:Record<string,string>={Soumis:'text-blue-400 bg-blue-500/10 border-blue-500/30','En cours':'text-yellow-400 bg-yellow-500/10 border-yellow-500/30','Approuvé':'text-green-400 bg-green-500/10 border-green-500/30','Clôturé':'text-slate-400 bg-slate-500/10 border-slate-500/30'};
              return(
                <motion.div key={s} whileHover={{scale:1.05}} initial={{opacity:0,y:8}} animate={{opacity:1,y:0}} transition={{delay:i*.07}}
                  className={`rounded-xl border p-3 text-center cursor-default ${colors[s]}`}>
                  <p className="font-bold text-xl"><CountUp end={count} duration={1} preserveValue/></p>
                  <p className="text-xs mt-0.5">{s}</p>
                </motion.div>
              );
            })}
          </div>

          {/* Kaizen backlog */}
          <div className="space-y-3">
            <h3 className="text-white font-semibold text-sm">Backlog Kaizen — Priorisé par impact</h3>
            {kaizen.sort((a,b)=>{const p:Record<string,number>={Haute:3,Moyenne:2,Faible:1};return p[b.priority]-p[a.priority];}).map((k,i)=>(
              <motion.div key={k.id} initial={{opacity:0,x:-12}} animate={{opacity:1,x:0}} transition={{delay:i*.04}}
                whileHover={{scale:1.005,boxShadow:k.priority==='Haute'?'0 0 12px rgba(239,68,68,0.1)':undefined}}
                className="rounded-xl border border-blue-900/40 p-4"
                style={{background:'linear-gradient(135deg,rgba(15,23,42,0.9) 0%,rgba(30,41,59,0.65) 100%)',backdropFilter:'blur(8px)'}}>
                <div className="flex items-start gap-3">
                  <motion.div className={`flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-bold border mt-0.5 ${k.priority==='Haute'?'bg-red-500/15 border-red-500/40 text-red-400':k.priority==='Moyenne'?'bg-yellow-500/15 border-yellow-500/40 text-yellow-400':'bg-green-500/15 border-green-500/40 text-green-400'}`}
                    animate={k.priority==='Haute'?{opacity:[1,.6,1]}:{}} transition={{duration:1.5,repeat:Infinity}}>
                    {k.priority}
                  </motion.div>
                  <div className="flex-1 min-w-0">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-2">
                      <div><p className="text-slate-400 text-xs mb-0.5 font-medium">Problème</p><p className="text-white text-xs">{k.problem}</p></div>
                      <div><p className="text-slate-400 text-xs mb-0.5 font-medium">Impact</p><p className="text-orange-300 text-xs">{k.impact}</p></div>
                      <div><p className="text-slate-400 text-xs mb-0.5 font-medium">Suggestion</p><p className="text-green-300 text-xs">{k.suggestion}</p></div>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-slate-500">
                      <span>{k.region}</span><span>·</span><span>{k.submittedBy}</span><span>·</span><span>{k.date}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <select value={k.status} onChange={e=>setKaizen(prev=>prev.map(x=>x.id===k.id?{...x,status:e.target.value as any}:x))}
                      className="bg-slate-700/60 border border-slate-600/40 rounded-lg text-xs text-slate-300 px-2 py-1 focus:outline-none">
                      {['Soumis','En cours','Approuvé','Clôturé'].map(s=><option key={s} value={s}>{s}</option>)}
                    </select>
                    <button onClick={()=>setKaizen(prev=>prev.filter(x=>x.id!==k.id))} className="p-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-colors"><Trash2 className="w-3 h-3"/></button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}
      </AnimatePresence>

      {/* Footer */}
      <div className="rounded-xl border border-blue-900/30 bg-slate-800/40 px-4 py-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-slate-400 text-xs italic">LEAN Six Sigma · Shewhart SPC · DMAIC · Kaizen · Actualisation temps réel</p>
        <div className="flex items-center gap-2"><Settings className="w-4 h-4 text-green-400"/><span className="text-green-300 text-xs font-medium">Amélioration continue · DGSN Cameroun</span></div>
      </div>
    </div>
  );
};
